import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
   PalletTypeCard,
   SectionContainer,
   SharedGridTable,
   SharedGridTableCell,
   SharedGridTableRow,
} from 'components/shared';
import { usePalletsTypes } from 'constants/palletTypes';
import { ClientUserDTO } from 'interfaces';
import { getDayLabel } from 'utils/functions';
import { currencyFormatter } from 'utils/numberFormatter';

import { useRentPeriodHeaders } from '../../ClientForm/ClientFormSteps/RatesConfigurationForm/RentPeriod/RentPeriod';

import PalletsPricing from './PalletsPricing/PalletsPricing';
import styles from './ClientDetailsPallets.module.css';

export const ClientDetailsPallets = (client: ClientUserDTO) => {
   const { t } = useTranslation();
   const { rateConfig, deliveryAddresses } = client;

   const { palletTypesData, PALLET_TYPES_LABELS_WITH_DIMENSIONS } = usePalletsTypes();

   const rentPeriodHeaders = useRentPeriodHeaders();

   const delaysChargesHeaders = useMemo(
      () => [
         t('clientDetailsPallets.delayLength'),
         ...(client.rateConfig
            ? client.rateConfig.delaysCharges.map(delayCharge => {
                 return (
                    <div className={styles.columnHeader}>
                       <h3>{PALLET_TYPES_LABELS_WITH_DIMENSIONS[delayCharge.palletType].label}</h3>
                       <p>
                          {PALLET_TYPES_LABELS_WITH_DIMENSIONS[delayCharge.palletType].dimensions}
                       </p>
                    </div>
                 );
              })
            : []),
      ],
      [PALLET_TYPES_LABELS_WITH_DIMENSIONS, client.rateConfig, t],
   );
   return (
      <>
         <SectionContainer style={{ marginTop: 24 }} title={t('clientDetailsPallets.palletTypes')}>
            <div className={styles.cardsContainer}>
               {palletTypesData.map(palletType => (
                  <PalletTypeCard
                     key={palletType.name}
                     palletType={palletType}
                     disabled={
                        !client.availablePalletTypes.some(
                           availablePalletType => availablePalletType === palletType.name,
                        )
                     }
                  />
               ))}
            </div>
         </SectionContainer>
         <SectionContainer title={t('clientDetailsPallets.ratesConfiguration')}>
            <PalletsPricing
               commonPalletsPricing={rateConfig?.palletRates || []}
               palletsPricingPerAddresses={deliveryAddresses}
            />
            <SectionContainer innerSection title={t('editRentalPeriod.title')}>
               <div className={styles.rentPeriodContainer}>
                  <p>{t('clientDetailsPallets.rentalPeriodDays')}</p>
                  <p>
                     {client.rateConfig?.retentionConfig.retentionPeriodDays}{' '}
                     {client.rateConfig &&
                        getDayLabel(client.rateConfig.retentionConfig.retentionPeriodDays)}
                  </p>
               </div>
               <SharedGridTable
                  headers={rentPeriodHeaders}
                  rows={[
                     client.rateConfig?.retentionConfig.retentionPalletCosts.map(
                        (retentionPalletCost, index) => (
                           <SharedGridTableRow key={index}>
                              <SharedGridTableCell>
                                 <h3>
                                    {
                                       PALLET_TYPES_LABELS_WITH_DIMENSIONS[
                                          retentionPalletCost.palletType
                                       ].label
                                    }
                                 </h3>
                                 <p>
                                    {
                                       PALLET_TYPES_LABELS_WITH_DIMENSIONS[
                                          retentionPalletCost.palletType
                                       ].dimensions
                                    }
                                 </p>
                              </SharedGridTableCell>
                              <SharedGridTableCell>
                                 {currencyFormatter.format(retentionPalletCost.price)}
                              </SharedGridTableCell>
                           </SharedGridTableRow>
                        ),
                     ),
                  ]}
               />
            </SectionContainer>
            <SectionContainer
               innerSection
               title={t('clientDetailsPallets.charges')}
               subtitle={t('clientDetailsPallets.chargesSubtitle')}
            >
               <SharedGridTable
                  headers={delaysChargesHeaders}
                  rows={[
                     <SharedGridTableRow>
                        <SharedGridTableCell>
                           <p className={styles.rowHeader}>{t('clientDetailsPallets.days.21')}</p>
                        </SharedGridTableCell>
                        {client.rateConfig?.delaysCharges.map((delayCharge, index) => {
                           return (
                              <SharedGridTableCell key={index}>
                                 {currencyFormatter.format(delayCharge.upTo21Days)}
                              </SharedGridTableCell>
                           );
                        })}
                     </SharedGridTableRow>,
                     <SharedGridTableRow>
                        <SharedGridTableCell>
                           <p className={styles.rowHeader}>{t('clientDetailsPallets.days.30')}</p>
                        </SharedGridTableCell>
                        {client.rateConfig?.delaysCharges.map((delayCharge, index) => {
                           return (
                              <SharedGridTableCell key={index}>
                                 {currencyFormatter.format(delayCharge.upTo30Days)}
                              </SharedGridTableCell>
                           );
                        })}
                     </SharedGridTableRow>,
                     <SharedGridTableRow>
                        <SharedGridTableCell>
                           <p className={styles.rowHeader}>
                              {t('clientDetailsPallets.days.over30')}
                           </p>
                        </SharedGridTableCell>
                        {client.rateConfig?.delaysCharges.map((delayCharge, index) => {
                           return (
                              <SharedGridTableCell key={index}>
                                 {currencyFormatter.format(delayCharge.over30Days)}
                              </SharedGridTableCell>
                           );
                        })}
                     </SharedGridTableRow>,
                  ]}
               />
            </SectionContainer>
         </SectionContainer>
      </>
   );
};
